// Profile Migration Utility
// Transforms old flat profile structure to new nested structure with mappings

import { PersonalityProfile } from '@/types/tps.types';
import { calculateSLOANType } from '@/mappings/sloan.enhanced';

/**
 * Detects serialization artifacts like { "_type": "undefined", "value": "undefined" }
 * These occur when undefined values get serialized to JSON incorrectly
 */
function isSerializationArtifact(value: any): boolean {
  if (!value || typeof value !== 'object') return false;
  return value._type === 'undefined' || value.value === 'undefined';
}

/**
 * Clean serialization artifacts from a profile object
 */
function cleanSerializationArtifacts(profile: any): any {
  if (!profile || typeof profile !== 'object') return profile;
  
  const cleaned = { ...profile };
  
  // Clean top-level artifacts
  if (isSerializationArtifact(cleaned.v3Metadata)) {
    cleaned.v3Metadata = undefined;
  }
  if (isSerializationArtifact(cleaned.frameworkInsights)) {
    cleaned.frameworkInsights = undefined;
  }
  
  return cleaned;
}

/**
 * Detects if a profile uses the old flat structure
 */
export function isOldProfileFormat(profile: any): boolean {
  if (!profile || typeof profile !== 'object') return false;
  
  // If it has mappings key, it's already new format
  if (profile.mappings) return false;
  
  // Check for old flat keys
  const oldFormatKeys = ['mbti', 'enneagram', 'bigFive', 'alignment', 'hollandCode', 'socionics', 'attachmentStyle', 'discProfile'];
  return oldFormatKeys.some(key => key in profile);
}

/**
 * Migrates old flat profile structure to new nested structure
 */
export function migrateProfileToNewFormat(profile: any): PersonalityProfile {
  if (!profile || typeof profile !== 'object') {
    return {
      dominantTraits: {},
      traitScores: {},
      domainScores: {
        External: 0,
        Internal: 0,
        Interpersonal: 0,
        Processing: 0
      },
      mappings: {
        mbti: '',
        enneagram: '',
        enneagramDetails: { type: 0, wing: 0, tritype: '' },
        bigFive: {},
        dndAlignment: '',
        socionics: '',
        hollandCode: '',
        personalityMatches: []
      },
      timestamp: new Date().toISOString()
    };
  }

  // Clean any serialization artifacts before processing
  const cleanedProfile = cleanSerializationArtifacts(profile);

  // If already has mappings, ensure complete structure and return
  if (cleanedProfile.mappings) {
    return ensureCompleteStructure(cleanedProfile);
  }

  // Transform flat to nested structure
  const migratedProfile: PersonalityProfile = {
    dominantTraits: cleanedProfile.dominantTraits || {},
    traitScores: cleanedProfile.traitScores || {},
    domainScores: cleanedProfile.domainScores || {
      External: 0,
      Internal: 0,
      Interpersonal: 0,
      Processing: 0
    },
    mappings: {
      mbti: cleanedProfile.mbti || '',
      enneagram: cleanedProfile.enneagram || '',
      enneagramDetails: cleanedProfile.enneagramDetails || { type: 0, wing: 0, tritype: '' },
      bigFive: cleanedProfile.bigFive || {},
      dndAlignment: cleanedProfile.alignment || cleanedProfile.dndAlignment || '',
      socionics: cleanedProfile.socionics || '',
      hollandCode: cleanedProfile.hollandCode || '',
      personalityMatches: cleanedProfile.personalityMatches || [],
      // Enhanced details (optional)
      mbtiDetail: cleanedProfile.mbtiDetail,
      enneagramDetail: cleanedProfile.enneagramDetail,
      bigFiveDetail: cleanedProfile.bigFiveDetail,
      sloanDetail: cleanedProfile.sloanDetail || (cleanedProfile.traitScores ? calculateSLOANType(cleanedProfile.traitScores) : undefined),
      alignmentDetail: cleanedProfile.alignmentDetail,
      hollandDetail: cleanedProfile.hollandDetail,
      attachmentStyle: cleanedProfile.attachmentStyle,
      attachmentDetail: cleanedProfile.attachmentDetail,
      socionicsDetail: cleanedProfile.socionicsDetail,
      discDetail: cleanedProfile.discDetail || cleanedProfile.discProfile,
      disc: cleanedProfile.disc,
      sloan: cleanedProfile.sloan,
      integralDetail: cleanedProfile.integralDetail || cleanedProfile.integral,
      // Extended frameworks
      hogwartsHouse: cleanedProfile.hogwartsHouse,
      hogwartsDetail: cleanedProfile.hogwartsDetail,
      loveLanguage: cleanedProfile.loveLanguage,
      loveLanguageDetail: cleanedProfile.loveLanguageDetail,
      trueColor: cleanedProfile.trueColor,
      trueColorDetail: cleanedProfile.trueColorDetail,
      classicalTemperament: cleanedProfile.classicalTemperament,
      temperamentDetail: cleanedProfile.temperamentDetail,
      keirsey: cleanedProfile.keirsey,
      keirseyDetail: cleanedProfile.keirseyDetail
    },
    v3Metadata: cleanedProfile.v3Metadata,
    frameworkInsights: cleanedProfile.frameworkInsights,
    timestamp: cleanedProfile.timestamp || new Date().toISOString(),
    version: cleanedProfile.version
  };

  // Preserve a root-level Integral record (Integral assessment writes it there).
  if (cleanedProfile.integralDetail) {
    (migratedProfile as any).integralDetail = cleanedProfile.integralDetail;
  }

  return migratedProfile;
}

/**
 * Ensures a profile has complete structure even if it's already in new format
 */
function ensureCompleteStructure(profile: any): PersonalityProfile {
  const complete: PersonalityProfile = {
    dominantTraits: profile.dominantTraits || {},
    traitScores: profile.traitScores || {},
    domainScores: profile.domainScores || {
      External: 0,
      Internal: 0,
      Interpersonal: 0,
      Processing: 0
    },
    mappings: {
      mbti: profile.mappings?.mbti || '',
      enneagram: profile.mappings?.enneagram || '',
      enneagramDetails: profile.mappings?.enneagramDetails || { type: 0, wing: 0, tritype: '' },
      bigFive: profile.mappings?.bigFive || {},
      dndAlignment: profile.mappings?.dndAlignment || '',
      socionics: profile.mappings?.socionics || '',
      hollandCode: profile.mappings?.hollandCode || '',
      personalityMatches: profile.mappings?.personalityMatches || [],
      // Enhanced details
      mbtiDetail: profile.mappings?.mbtiDetail,
      enneagramDetail: profile.mappings?.enneagramDetail,
      bigFiveDetail: profile.mappings?.bigFiveDetail,
      sloanDetail: profile.mappings?.sloanDetail || (profile.traitScores ? calculateSLOANType(profile.traitScores) : undefined),
      alignmentDetail: profile.mappings?.alignmentDetail,
      hollandDetail: profile.mappings?.hollandDetail,
      attachmentStyle: profile.mappings?.attachmentStyle,
      attachmentDetail: profile.mappings?.attachmentDetail,
      socionicsDetail: profile.mappings?.socionicsDetail,
      discDetail: profile.mappings?.discDetail,
      disc: profile.mappings?.disc,
      sloan: profile.mappings?.sloan,
      integralDetail: profile.mappings?.integralDetail,
      // Some assessments (Integral) persist the record at the profile root —
      // keep it reachable instead of silently dropping it.
      ...(profile.integralDetail && !profile.mappings?.integralDetail
        ? { integralDetail: profile.integralDetail }
        : {}),
      // Extended frameworks
      hogwartsHouse: profile.mappings?.hogwartsHouse,
      hogwartsDetail: profile.mappings?.hogwartsDetail,
      loveLanguage: profile.mappings?.loveLanguage,
      loveLanguageDetail: profile.mappings?.loveLanguageDetail,
      trueColor: profile.mappings?.trueColor,
      trueColorDetail: profile.mappings?.trueColorDetail,
      classicalTemperament: profile.mappings?.classicalTemperament,
      temperamentDetail: profile.mappings?.temperamentDetail,
      keirsey: profile.mappings?.keirsey,
      keirseyDetail: profile.mappings?.keirseyDetail
    },
    v3Metadata: profile.v3Metadata,
    frameworkInsights: profile.frameworkInsights,
    timestamp: profile.timestamp || new Date().toISOString(),
    version: profile.version
  };

  if (profile.integralDetail) {
    (complete as any).integralDetail = profile.integralDetail;
  }

  return complete;
}

/**
 * Batch migrate multiple profiles
 */
export function migrateProfiles(profiles: any[]): PersonalityProfile[] {
  return profiles.map(profile => migrateProfileToNewFormat(profile));
}
